import { AlertCircleIcon, CalendarIcon } from "lucide-react";

import { Skeleton, Tag } from "@/modules/common/components";
import { ANEEL_TARIFF_IMPORT_STATUS_OPTIONS, AneelTariffImportHistoryStatus } from "@/modules/common/types/AneelTariffs.enum";
import { AdminAneelTariffLastUpdateResponse } from "@/service/response/admin-aneel-tariffs";
import { formatDate } from "@/modules/common/utils/format";

interface LastUpdateCardProps {
  data?: AdminAneelTariffLastUpdateResponse;
  isLoading?: boolean;
  isError?: boolean;
}

const STATUS_TAG_VARIANT: Record<AneelTariffImportHistoryStatus, "success" | "warning" | "danger"> = {
  [AneelTariffImportHistoryStatus.SUCCESS]: "success",
  [AneelTariffImportHistoryStatus.PARTIAL]: "warning",
  [AneelTariffImportHistoryStatus.FAILED]: "danger",
};

const LastUpdateCard = ({ data, isLoading, isError }: LastUpdateCardProps) => {
  if (isLoading) {
    return (
      <div className="w-full bg-white rounded-lg shadow p-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-8">
        <Skeleton className="h-14 w-14 rounded-full shrink-0" />
        <div className="flex-1 flex flex-col gap-2">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full bg-white rounded-lg shadow p-6 flex items-center gap-4">
        <div className="bg-red-50 rounded-full p-3 shrink-0">
          <AlertCircleIcon className="text-red-600 w-6 h-6" />
        </div>
        <p className="text-sm text-ui-text-muted">
          Não foi possível carregar a última atualização das tarifas. Tente novamente mais tarde.
        </p>
      </div>
    );
  }

  if (!data || !data.hasUpdate) {
    return (
      <div className="w-full bg-white rounded-lg shadow p-6 flex items-center gap-4">
        <div className="bg-ui-hover rounded-full p-3 shrink-0">
          <CalendarIcon className="text-ui-text-muted w-6 h-6" />
        </div>
        <p className="text-sm text-ui-text-muted">Nenhuma importação de tarifas foi concluída com sucesso até o momento.</p>
      </div>
    );
  }

  const statusLabel = ANEEL_TARIFF_IMPORT_STATUS_OPTIONS.find((option) => option.value === String(data.status))?.label ?? "-";

  return (
    <div className="w-full bg-white rounded-lg shadow p-6 flex flex-col gap-6 lg:flex-row lg:items-center">
      <div className="flex items-center gap-4">
        <div className="bg-lime-50 rounded-full p-3 shrink-0">
          <CalendarIcon className="text-lime-600 w-6 h-6" />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-sm text-ui-text-muted">Última atualização das tarifas</span>
          <span className="text-lg font-bold text-ui-primary">
            {data.updatedAt ? formatDate(data.updatedAt, "DD/MM/YYYY [às] HH:mm") : "-"}
          </span>
          {data.status !== null && <Tag className="mt-1 w-fit" label={statusLabel} variant={STATUS_TAG_VARIANT[data.status]} />}
        </div>
      </div>

      <div className="flex flex-col gap-1 lg:pl-8 lg:border-l lg:border-ui-border">
        <span className="text-sm text-ui-text-muted">Arquivo importado</span>
        <span className="text-sm font-medium text-ui-navy-dark">{data.fileName ?? "-"}</span>
      </div>

      <div className="flex flex-col gap-1 lg:pl-8 lg:border-l lg:border-ui-border">
        <span className="text-sm text-ui-text-muted">Registros processados</span>
        <span className="text-sm font-medium text-ui-navy-dark">{data.countCreated ?? "-"}</span>
      </div>

      <div className="flex flex-col gap-1 lg:pl-8 lg:border-l lg:border-ui-border">
        <span className="text-sm text-ui-text-muted">Responsável</span>
        <span className="text-sm font-medium text-ui-navy-dark">{data.importedByAdminUserName ?? "-"}</span>
      </div>
    </div>
  );
};

export default LastUpdateCard;
